// SolarNEST Ground Mount mounting system
// Supports East/West tilt and Triple tilt

import { StudioSystemType } from 'types/global'
import { FeatureFlagName } from 'util/featureFlags'
import { BomGeneratorAbstract } from '../bom_generators/BomGeneratorAbstract'
import { getPanelLayoutDimensions, hasUniformRowLengths } from '../compatibilityLayout'
import {
  BasicSpacingLayoutAdjusterAbstract,
  SpacingRuleSet,
} from '../layout_adjusters/BasicSpacingLayoutAdjusterAbstract'
import { MountingSystemAbstract } from '../MountingSystemAbstract'
import { CompatibilityParameters, Fastener, Item, MountingCalcResult, MountingComponentType } from '../types'
import { addItems, createMountingItem } from '../utils'
import { SOLARNEST_REQUIRED_TILT_DEGREES } from './solarNestWindSnowMax'

const spacingRuleSet: SpacingRuleSet = {
  interPanel: { horizontalSpacing: 20, verticalSpacing: 20 },
}

class SolarNestGroundMountSpacingAdjuster extends BasicSpacingLayoutAdjusterAbstract {
  spacingRuleSet = spacingRuleSet
}

class SolarNestGroundMountBomGenerator extends BomGeneratorAbstract {
  async generateBom(result: MountingCalcResult): Promise<MountingCalcResult> {
    const tiltOption = this.input.options.tiltOption as string | undefined
    if (tiltOption !== 'eastWestTilt' && tiltOption !== 'tripleTilt') {
      return {
        ...result,
        items: [],
        fasteners: {},
      }
    }

    const isTripleTilt = tiltOption === 'tripleTilt'
    const totalPanels = this.countCurrentBlockPanels()
    const modulesPerRow = this.countModulesPerRow()
    const panelLayout = this.getPanelLayout()
    const panelDimensions = this.getPanelDimensions()

    const items: Item[] = []

    // Purlin: modules per row + 1
    const purlinQty = modulesPerRow + 1
    items.push(...this.createPurlin(purlinQty))

    // Rebar rods: based on panel dimensions and tilt option
    const rebarRods = this.calculateRebarRods(purlinQty, panelDimensions, isTripleTilt)
    items.push(...rebarRods)

    // Base plates: 1 per rebar rod
    const basePlateQty = rebarRods.length
    items.push(...this.createBasePlate(basePlateQty))

    // Top plates: 1 per base plate
    // Triple tilt uses SNT TOP PLATE, East/West uses SN TOP PLATE
    items.push(...this.createTopPlate(basePlateQty, isTripleTilt))

    // Anchors (F120-Q-Z6 KIT): based on panel layout
    const anchorQty = this.calculateAnchorQuantity(panelLayout)
    items.push(...this.createAnchor(anchorQty))

    // Tilt brackets: purlins × 4
    const tiltBracketQty = purlinQty * 4
    items.push(...this.createTiltBracket(tiltBracketQty))

    // Top clamp assembly: tilt brackets * 2 - 8
    const topClampQty = Math.max(0, tiltBracketQty * 2 - 8)
    items.push(...this.createTopClampAssembly(topClampQty))

    // Cables: N/S and E/W based on panel layout and size
    const cables = this.calculateCables(panelLayout, panelDimensions)
    items.push(...cables)

    // Fasteners
    items.push(...this.createM12x82mmSerratedFlangeHexBolt(tiltBracketQty))
    items.push(...this.createM12x38mmSerratedFlangeHexBolt(basePlateQty * 2))
    items.push(...this.createM12SerratedFlangeNut(tiltBracketQty + basePlateQty * 2))
    items.push(...this.createM12FlatWasher(basePlateQty * 2))

    return addItems(result, items)
  }

  private countCurrentBlockPanels(): number {
    let count = 0
    this.block.rows.forEach((row) => {
      row.forEach((panel) => {
        if (panel.isActive) {
          count++
        }
      })
    })
    return count
  }

  private countModulesPerRow(): number {
    if (!this.block.rows.length) {
      return 0
    }
    // System always represents layout as landscape-oriented, so rows represent horizontal span
    return this.block.rows.length
  }

  private getPanelLayout(): { columns: number; rows: number } {
    const numColumns = this.block.rows[0]?.length || 0
    const numRows = this.block.rows.length || 0
    return { columns: numColumns, rows: numRows }
  }

  private getPanelDimensions(): { lengthIn: number; widthIn: number } {
    const panel = this.block.panel
    const M_TO_IN = 39.3701
    // Panel dimensions in meters, convert to inches
    // length is the longer dimension, width is shorter
    const lengthM = Math.max(panel.width / 1000, panel.height / 1000) // Convert mm to m
    const widthM = Math.min(panel.width / 1000, panel.height / 1000)
    return {
      lengthIn: lengthM * M_TO_IN,
      widthIn: widthM * M_TO_IN,
    }
  }

  private calculateRebarRods(
    purlinQty: number,
    panelDimensions: { lengthIn: number; widthIn: number },
    isTripleTilt: boolean
  ): Item[] {
    const items: Item[] = []
    const isSmallPanel = panelDimensions.lengthIn <= 67.8 && panelDimensions.widthIn <= 44.65
    const rodsPerPurlin = isSmallPanel ? 2 : 3

    if (isTripleTilt) {
      // Triple tilt: half rods at 72", half at 84" per purlin
      // valley south/middle + peak south = 72"; valley north + peak middle/north = 84"
      const shortRods = Math.floor(rodsPerPurlin / 2)
      const longRods = rodsPerPurlin - shortRods
      for (let p = 0; p < purlinQty; p++) {
        for (let i = 0; i < shortRods; i++) {
          items.push(...this.createRebarRod('72'))
        }
        for (let i = 0; i < longRods; i++) {
          items.push(...this.createRebarRod('84'))
        }
      }
    } else {
      // East/West tilt: all rods same length
      const rodLength = '72'
      const totalRods = purlinQty * rodsPerPurlin
      for (let i = 0; i < totalRods; i++) {
        items.push(...this.createRebarRod(rodLength))
      }
    }

    return items
  }

  private calculateAnchorQuantity(panelLayout: { columns: number; rows: number }): number {
    // Anchor quantity based on panel layout (rows)
    // 2x4: 2 anchors, 2x6: 3, 2x8: 4, 2x10: 5, 2x12: 6, 2x14: 7, 2x16: 8
    const rowCount = panelLayout.rows
    if (rowCount <= 4) return 2
    if (rowCount <= 6) return 3
    if (rowCount <= 8) return 4
    if (rowCount <= 10) return 5
    if (rowCount <= 12) return 6
    if (rowCount <= 14) return 7
    return 8
  }

  private calculateCables(
    panelLayout: { columns: number; rows: number },
    panelDimensions: { lengthIn: number; widthIn: number }
  ): Item[] {
    const items: Item[] = []
    const rowCount = panelLayout.rows

    // Determine cable lengths based on panel size
    // ≤ 89.7" × 44.65": N/S = 15ft, E/W = 15ft (spec says 10ft but component code is 15ft or 20ft)
    // > 89.7" × 44.65": N/S = 15ft, E/W = 20ft
    // Component codes: UNV-151-12-290-15' (N/S), UNV-151-12-290-20' (E/W)
    const isSmallPanel = panelDimensions.lengthIn <= 89.7 && panelDimensions.widthIn <= 44.65
    const nsCableLength = '15'
    const ewCableLength = isSmallPanel ? '15' : '20'

    // E/W cables: 4 total (for outside bays)
    const ewCableQty = 4
    items.push(...this.createEWCable(ewCableLength, ewCableQty))

    // N/S cables: Increase by 2 per layout increment
    // 2x4: 2, 2x6: 4, 2x8: 6, etc.
    // Formula: rows - 2 (for 2x4 with 4 rows: 4-2=2, for 2x6 with 6 rows: 6-2=4)
    const nsCableQty = rowCount - 2
    items.push(...this.createNSCable(nsCableLength, nsCableQty))

    return items
  }

  private createPurlin(quantity: number): Item[] {
    const items: Item[] = []
    const template: Item = {
      name: 'SN-121-158-215-162',
      components: [
        {
          name: 'SN-121-158-215-162',
          type: MountingComponentType.OTHER,
          left: 0,
          top: 0,
        },
      ],
    }
    for (let i = 0; i < quantity; i++) {
      items.push(createMountingItem(template, { top: 0, left: 0, blockIndex: this.blockIndex }))
    }
    return items
  }

  private createRebarRod(length: string): Item[] {
    const items: Item[] = []
    const code = `SN-122-58-235-${length}`
    const template: Item = {
      name: code,
      components: [
        {
          name: code,
          type: MountingComponentType.OTHER,
          left: 0,
          top: 0,
        },
      ],
    }
    items.push(createMountingItem(template, { top: 0, left: 0, blockIndex: this.blockIndex }))
    return items
  }

  private createBasePlate(quantity: number): Item[] {
    const items: Item[] = []
    const template: Item = {
      name: 'SN-111-11-124-5',
      components: [
        {
          name: 'SN-111-11-124-5',
          type: MountingComponentType.OTHER,
          left: 0,
          top: 0,
        },
      ],
    }
    for (let i = 0; i < quantity; i++) {
      items.push(createMountingItem(template, { top: 0, left: 0, blockIndex: this.blockIndex }))
    }
    return items
  }

  private createTopPlate(quantity: number, isTripleTilt: boolean): Item[] {
    const items: Item[] = []
    const code = isTripleTilt ? 'SNT-112-11-124-7X3"' : 'SN-112-11-124-7X3"'
    const template: Item = {
      name: code,
      components: [
        {
          name: code,
          type: MountingComponentType.OTHER,
          left: 0,
          top: 0,
        },
      ],
    }
    for (let i = 0; i < quantity; i++) {
      items.push(createMountingItem(template, { top: 0, left: 0, blockIndex: this.blockIndex }))
    }
    return items
  }

  private createAnchor(quantity: number): Item[] {
    const items: Item[] = []
    const template: Item = {
      name: 'UNV-600-001-02',
      components: [
        {
          name: 'UNV-600-001-02',
          type: MountingComponentType.OTHER,
          left: 0,
          top: 0,
        },
      ],
    }
    for (let i = 0; i < quantity; i++) {
      items.push(createMountingItem(template, { top: 0, left: 0, blockIndex: this.blockIndex }))
    }
    return items
  }

  private createTiltBracket(quantity: number): Item[] {
    const items: Item[] = []
    const template: Item = {
      name: 'SN-121-00-966',
      components: [
        {
          name: 'SN-121-00-966',
          type: MountingComponentType.OTHER,
          left: 0,
          top: 0,
        },
      ],
    }
    for (let i = 0; i < quantity; i++) {
      items.push(createMountingItem(template, { top: 0, left: 0, blockIndex: this.blockIndex }))
    }
    return items
  }

  private createTopClampAssembly(quantity: number): Item[] {
    const items: Item[] = []
    const template: Item = {
      name: 'SN-111-00-500',
      components: [
        {
          name: 'SN-111-00-500',
          type: MountingComponentType.CLAMP,
          left: 0,
          top: 0,
        },
      ],
    }
    for (let i = 0; i < quantity; i++) {
      items.push(createMountingItem(template, { top: 0, left: 0, blockIndex: this.blockIndex }))
    }
    return items
  }

  private createEWCable(length: string, quantity: number): Item[] {
    const items: Item[] = []
    const code = `UNV-151-12-290-${length}'`
    const template: Item = {
      name: code,
      components: [
        {
          name: code,
          type: MountingComponentType.OTHER,
          left: 0,
          top: 0,
        },
      ],
    }
    for (let i = 0; i < quantity; i++) {
      items.push(createMountingItem(template, { top: 0, left: 0, blockIndex: this.blockIndex }))
    }
    return items
  }

  private createNSCable(length: string, quantity: number): Item[] {
    const items: Item[] = []
    const code = `UNV-151-12-290-${length}'`
    const template: Item = {
      name: code,
      components: [
        {
          name: code,
          type: MountingComponentType.OTHER,
          left: 0,
          top: 0,
        },
      ],
    }
    for (let i = 0; i < quantity; i++) {
      items.push(createMountingItem(template, { top: 0, left: 0, blockIndex: this.blockIndex }))
    }
    return items
  }

  private createM12x82mmSerratedFlangeHexBolt(quantity: number): Item[] {
    const items: Item[] = []
    const template: Item = {
      name: 'HDW-SFB-M12-82-762',
      components: [
        {
          name: 'HDW-SFB-M12-82-762',
          type: MountingComponentType.OTHER,
          left: 0,
          top: 0,
        },
      ],
    }
    for (let i = 0; i < quantity; i++) {
      items.push(createMountingItem(template, { top: 0, left: 0, blockIndex: this.blockIndex }))
    }
    return items
  }

  private createM12x38mmSerratedFlangeHexBolt(quantity: number): Item[] {
    const items: Item[] = []
    const template: Item = {
      name: 'HDW-SFB-M12-38-761',
      components: [
        {
          name: 'HDW-SFB-M12-38-761',
          type: MountingComponentType.OTHER,
          left: 0,
          top: 0,
        },
      ],
    }
    for (let i = 0; i < quantity; i++) {
      items.push(createMountingItem(template, { top: 0, left: 0, blockIndex: this.blockIndex }))
    }
    return items
  }

  private createM12SerratedFlangeNut(quantity: number): Item[] {
    const items: Item[] = []
    const template: Item = {
      name: 'HDW-SFN-M12-76',
      components: [
        {
          name: 'HDW-FW-M12-76',
          type: MountingComponentType.OTHER,
          left: 0,
          top: 0,
        },
      ],
    }
    for (let i = 0; i < quantity; i++) {
      items.push(createMountingItem(template, { top: 0, left: 0, blockIndex: this.blockIndex }))
    }
    return items
  }

  private createM12FlatWasher(quantity: number): Item[] {
    const items: Item[] = []
    const template: Item = {
      name: 'HDW-FW-M12-76',
      components: [
        {
          name: 'HDW-FW-M12-76',
          type: MountingComponentType.OTHER,
          left: 0,
          top: 0,
        },
      ],
    }
    for (let i = 0; i < quantity; i++) {
      items.push(createMountingItem(template, { top: 0, left: 0, blockIndex: this.blockIndex }))
    }
    return items
  }
}

export class SolarNestGroundMount extends MountingSystemAbstract {
  layoutAdjusterClass = SolarNestGroundMountSpacingAdjuster
  bomGeneratorClass = SolarNestGroundMountBomGenerator

  getFastener(): Fastener {
    // Ground mount doesn't use roof hooks/fasteners
    return {
      includedInRoofHookProduct: false,
      name: '',
      qtyPerRoofHook: 0,
      length: 0,
      diameter: 0,
      components: [],
    }
  }

  getCompatibilityParameters(): CompatibilityParameters {
    return {
      integrations: [],
      moduleThicknessRange: [30, 46],
      roofTypeRequired: false, // Ground mount doesn't require roof type
      featureFlag: 'mounting_solarnest_groundmount' as FeatureFlagName,
      panelLayoutRange: { columns: [2, 2], rows: [4, 16] },
    }
  }
}

export function validateSolarNestGroundMountCompatibility(system: StudioSystemType): boolean {
  const mountingName = system?.mounting
  if (mountingName !== 'solarNestGroundMount') return true

  window.WorkspaceHelper.removeProjectErrorFromReduxStore(`${mountingName.toUpperCase()}_PANEL_SETUP_INFO`, system.uuid, 'plugin')
  window.WorkspaceHelper.removeProjectErrorFromReduxStore(`${mountingName.toUpperCase()}_FACING_INFO`, system.uuid, 'plugin')
  window.WorkspaceHelper.removeProjectErrorFromReduxStore(`${mountingName.toUpperCase()}_INPUT_WARNING`, system.uuid, 'plugin')

  if (system.moduleGrids().length === 0) {
    window.WorkspaceHelper.addProjectErrorToReduxStore({
      message: window.translate(
        'Panels must be placed first. Please deselect SolarNEST and add panels using Dual Tilt racking type and Landscape panel orientation in panel settings'
      ),
      key: `${mountingName.toUpperCase()}_PANEL_SETUP_INFO`,
      severity: 'warning',
      systemId: system.uuid,
      source: 'plugin',
      category: 'mounting',
      options: {},
    })
    return true
  }

  const allGridsValid = system.moduleGrids().every((moduleGrid) => {
    const { numColumns, numRows } = getPanelLayoutDimensions(moduleGrid.cellsActive)
    const isUniform = hasUniformRowLengths(moduleGrid.cellsActive)
    const hasEvenColumns = numColumns % 2 === 0
    const hasEvenRows = numRows % 2 === 0
    return isUniform && hasEvenColumns && hasEvenRows
  })

  if (!allGridsValid) {
    window.WorkspaceHelper.addProjectErrorToReduxStore({
      message: window.translate('Panel layout must be between 2x4 and 2x16 with an even number of columns'),
      key: `${mountingName.toUpperCase()}_PLUGIN_WARNING`,
      severity: 'warning',
      systemId: system.uuid,
      source: 'plugin',
      category: 'mounting',
      options: {},
    })
    return false
  }

  const integrationData = system.integration_json?.solarNestGroundMount

  // Ground slope validation
  const groundSlope = integrationData?.groundSlope as number | undefined
  if (groundSlope !== undefined) {
    const grids = system.moduleGrids()
    if (grids.length > 0) {
      const firstGrid = grids[0]
      const { numRows } = getPanelLayoutDimensions(firstGrid.cellsActive)
      const maxSlope = numRows >= 10 ? 1 : 2 // 2x10-2x16: max 1°, 2x4-2x8: max 2°
      if (groundSlope > maxSlope) {
        window.WorkspaceHelper.addProjectErrorToReduxStore({
          message: window.translate(
            'Slope exceeds maximum value of %{maxSlope} degrees, please adjust in the Ground slope input field',
            { maxSlope }
          ),
          key: `${mountingName.toUpperCase()}_INPUT_WARNING`,
          severity: 'warning',
          systemId: system.uuid,
          source: 'plugin',
          category: 'mounting',
          options: {},
        })
        return false
      }
    }
  }

  const groundClearance = integrationData?.groundClearance as number | undefined
  if (groundClearance !== undefined && (groundClearance < 13 || groundClearance > 25)) {
    window.WorkspaceHelper.addProjectErrorToReduxStore({
      message: window.translate('Ground clearance is outside of compatible values. Please adjust in the Ground clearance input field'),
      key: `${mountingName.toUpperCase()}_INPUT_WARNING`,
      severity: 'warning',
      systemId: system.uuid,
      source: 'plugin',
      category: 'mounting',
      options: {},
    })
    return false
  }

  const frostLineDepth = integrationData?.frostLineDepth as string | undefined
  if (frostLineDepth === 'Greater than 36"') {
    window.WorkspaceHelper.addProjectErrorToReduxStore({
      message: window.translate('SolarNEST is incompatible with frost lines deeper than 36"'),
      key: `${mountingName.toUpperCase()}_INPUT_WARNING`,
      severity: 'warning',
      systemId: system.uuid,
      source: 'plugin',
      category: 'mounting',
      options: {},
    })
    return false
  }

  const soilType = integrationData?.soilType as string | undefined
  if (soilType === 'Rocky Conditions' || soilType === 'Bedrock') {
    window.WorkspaceHelper.addProjectErrorToReduxStore({
      message: window.translate(
        'Mounting system is incompatible with soil type "%{soilType}". Compatible soil types: Dense Clay, Fine Sand, Sandy Loam, Loamy, Hard Silts/Clays, Unknown.',
        { soilType }
      ),
      key: `${mountingName.toUpperCase()}_PLUGIN_WARNING`,
      severity: 'warning',
      systemId: system.uuid,
      source: 'plugin',
      category: 'mounting',
      options: {},
    })
    return false
  }

  const tiltOption = integrationData?.tiltOption as string | undefined
  if (tiltOption === 'eastWestTilt') {
    window.WorkspaceHelper.addProjectErrorToReduxStore({
      message: window.translate('Selected mounting system is designed to have dual tilt facing East/West'),
      key: `${mountingName.toUpperCase()}_FACING_INFO`,
      severity: 'info',
      systemId: system.uuid,
      source: 'plugin',
      category: 'mounting',
      options: {},
    })
  }

  if (tiltOption === 'eastWestTilt' || tiltOption === 'tripleTilt') {
    const tiltMismatch = system.moduleGrids().some((moduleGrid) => {
      const slope = Math.round(moduleGrid.getSlope() * 10) / 10
      const tilt = Math.round((moduleGrid.panelTiltOverride ?? 0) * 10) / 10
      const relativeTilt = Math.round((tilt - slope) * 10) / 10
      if (relativeTilt !== SOLARNEST_REQUIRED_TILT_DEGREES) return true
      if (tiltOption === 'tripleTilt') {
        const roll =
          Math.round((typeof (moduleGrid as any).getRoll === 'function' ? (moduleGrid as any).getRoll() : 0) * 10) / 10
        if (roll !== SOLARNEST_REQUIRED_TILT_DEGREES) return true
      }
      return false
    })
    if (tiltMismatch) {
      const message =
        tiltOption === 'tripleTilt'
          ? window.translate(
              'Selected mounting system requires relative tilt to be %{relativeTilt} degrees and triple tilt to be between 0-%{tripleTilt} degrees',
              { relativeTilt: SOLARNEST_REQUIRED_TILT_DEGREES, tripleTilt: SOLARNEST_REQUIRED_TILT_DEGREES }
            )
          : window.translate('Selected mounting system requires relative tilt to be %{degrees} degrees', {
              degrees: SOLARNEST_REQUIRED_TILT_DEGREES,
            })
      window.WorkspaceHelper.addProjectErrorToReduxStore({
        message,
        key: `${mountingName.toUpperCase()}_INPUT_WARNING`,
        severity: 'warning',
        systemId: system.uuid,
        source: 'plugin',
        category: 'mounting',
        options: {},
      })
      return false
    }
  }

  return true
}
